import {useMemo} from 'react'
import {CardEnum, PlayerColor} from '@colonist/game-states'
import {UIGamePlayerInfoSectionContentHandProps} from '../../UIGamePlayerInfo'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators'
import {useMechanicValidatorDevelopmentCard} from '../../../store/selectors/useMechanicValidatorDevelopmentCard'
import {useMechanicValidatorProgressCards} from '../../../store/selectors/useMechanicValidatorProgressCards'
import {useMechanicValidatorLargestArmy} from '../../../store/selectors/useMechanicValidatorLargestArmy'
import {useMechanicValidatorKnight} from '../../../store/selectors/useMechanicValidatorKnight'
import {useMechanicValidatorLongestRoad} from '../../../store/selectors/useMechanicValidatorLongestRoad'

export const usePlayerInfoSectionContentHandProps = (playerColor: PlayerColor) => {
    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(playerColor)
    const resourceCardCount = playerValidator.resourceCards.getNumberOfCardsInInventory()
    const isResourceCardCountOverLimit = resourceCardCount > playerValidator.cardDiscardLimit

    const developmentCardsValidator = useMechanicValidatorDevelopmentCard()
    const developmentCardCount = developmentCardsValidator?.getPlayersTotalDevelopmentCards(playerColor)

    const progressCardsValidator = useMechanicValidatorProgressCards()
    const progressCardCount = progressCardsValidator?.getPlayersTotalProgressCards(playerColor)

    const largestArmyValidator = useMechanicValidatorLargestArmy()
    const largestArmyCount = developmentCardsValidator?.getPlayersTotalUsedDevelopmentCardsOfType(playerColor, CardEnum.Knight)
    const hasLargestArmy = largestArmyValidator?.getPlayerWithLargestArmy() === playerColor

    const knightValidator = useMechanicValidatorKnight()
    const knightCount = knightValidator?.getKnightPlayerValidator(playerColor).getPlayerKnightStrength()
    const hasHighestKnightStrength = knightValidator?.getPlayersWithHighestKnightStrength().includes(playerColor)

    const longestRoadValidator = useMechanicValidatorLongestRoad()
    const longestRoadCount = longestRoadValidator?.getLongestRoadPlayerState(playerColor)?.longestRoad
    const hasLongestRoad = longestRoadValidator?.getPlayerWithLongestRoadCard() === playerColor

    return useMemo<UIGamePlayerInfoSectionContentHandProps>(() => ({
        resourceCardCount: resourceCardCount,
        resourceCardDiscardLimit: playerValidator.cardDiscardLimit,
        developmentCardCount: developmentCardCount,
        progressCardCount: progressCardCount,
        largestArmyCount: largestArmyCount,
        highlightLargestArmy: hasLargestArmy,
        defenderOfColonistCount: knightCount,
        highlightDefenderOfColonist: hasHighestKnightStrength && (knightCount ?? 0) > 0,
        longestRoadCount: longestRoadCount,
        highlightLongestRoad: hasLongestRoad,
    }), [
        resourceCardCount,
        isResourceCardCountOverLimit,
        developmentCardCount,
        progressCardCount,
        largestArmyCount,
        hasLargestArmy,
        knightCount,
        hasHighestKnightStrength,
        longestRoadCount,
        hasLongestRoad,
    ])
}
